import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  resolveEmailBuilderCanvasBlockFormattingRailActions,
  type TEmailBuilderCanvasBlockFormattingRailTextVariant,
  type TEmailBuilderCanvasBlockRailAction,
} from '../editing/emailBuilderCanvasBlockInteractions';
import {
  railActionIcon,
  railActionTooltip,
} from '../editing/emailBuilderCanvasBlockRail';
import { EMAIL_BUILDER_FORMATTING_RAIL_ATTR } from './formattingRailConstants';
import {
  beginFormattingRailToolbarPress,
  endFormattingRailToolbarPress,
} from './formattingRailToolbarPress';
import { useFormattingRail } from './useFormattingRail';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useStableScopedDomId } from '../useStableDomId';

function isMacPlatform(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
  );
}

type FormattingRailButtonProps = {
  blockId: string;
  action: TEmailBuilderCanvasBlockRailAction;
  tooltip: string;
  ariaLabel: string;
  active?: boolean;
  disabled?: boolean;
  linkPopoverOpen?: boolean;
  onClick: () => void;
  onMouseDown: (event: React.MouseEvent) => void;
};

function FormattingRailButton({
  blockId,
  action,
  tooltip,
  ariaLabel,
  active = false,
  disabled = false,
  linkPopoverOpen = false,
  onClick,
  onMouseDown,
}: FormattingRailButtonProps) {
  const tooltipId = useStableScopedDomId('email-builder-formatting-rail', action);

  const button = (
    <button
      type="button"
      className={"email-builder-canvas-rail-btn"}
      data-active={active ? 'true' : undefined}
      aria-label={ariaLabel}
      aria-pressed={active}
      aria-disabled={disabled ? true : undefined}
      disabled={disabled}
      aria-expanded={action === 'link' ? linkPopoverOpen : undefined}
      aria-haspopup={action === 'link' ? 'dialog' : undefined}
      data-email-builder-link-trigger={action === 'link' ? blockId : undefined}
      data-email-builder-merge-tag-trigger={
        action === 'merge-tag' ? blockId : undefined
      }
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
      onMouseDown={event => {
        event.stopPropagation();
        beginFormattingRailToolbarPress(blockId);
        onMouseDown(event);
      }}
      onPointerUp={() => endFormattingRailToolbarPress(blockId)}
      onPointerCancel={() => endFormattingRailToolbarPress(blockId)}>
      <FontAwesomeIcon icon={railActionIcon(action)} aria-hidden />
    </button>
  );

  return (
    <OverlayTrigger
      placement="top"
      flip
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
      {disabled ? <span className={"email-builder-canvas-rail-tooltip-target"}>{button}</span> : button}
    </OverlayTrigger>
  );
}

type FormattingRailProps = {
  blockId: string;
  typeLabel: string;
  textVariant: TEmailBuilderCanvasBlockFormattingRailTextVariant;
  layout?: 'toolbar' | 'inline';
};

export function FormattingRail({
  blockId,
  typeLabel,
  textVariant,
  layout = 'toolbar',
}: FormattingRailProps) {
  const { t } = useTranslation();
  const isMac = isMacPlatform();
  const ariaLabel = t('{{type}} formatting', { type: typeLabel });
  const actions = resolveEmailBuilderCanvasBlockFormattingRailActions(textVariant);
  const {
    railRef,
    handleAction,
    handleActionMouseDown,
    isActionActive,
    isActionDisabled,
    linkPopover,
    linkPopoverOpen,
  } = useFormattingRail(blockId);

  const buttons = actions.reduce<React.ReactNode[]>((accumulator, action) => {
    const button = (
      <FormattingRailButton
        key={action}
        blockId={blockId}
        action={action}
        tooltip={railActionTooltip(action, t, isMac)}
        ariaLabel={railActionTooltip(action, t, isMac)}
        active={isActionActive(action)}
        disabled={isActionDisabled(action)}
        linkPopoverOpen={action === 'link' ? linkPopoverOpen : false}
        onClick={() => handleAction(action)}
        onMouseDown={event => handleActionMouseDown(action, event)}
      />
    );

    if (action !== 'numbered-list' && action !== 'merge-tag') {
      accumulator.push(button);
      return accumulator;
    }

    accumulator.push(
      <div
        key={`formatting-rail-separator-${action}`}
        className="email-builder-canvas-touch-rail__separator"
        aria-hidden
      />,
      button,
    );
    return accumulator;
  }, []);

  if (layout === 'inline') {
    return (
      <>
        <div
          ref={railRef}
          className={"email-builder-canvas-rail-inline-group"}
          data-editor-chrome
          data-text-toolbar
          role="group"
          aria-label={ariaLabel}
          data-email-builder-formatting-actions={blockId}
          {...{ [EMAIL_BUILDER_FORMATTING_RAIL_ATTR]: '' }}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}>
          {buttons}
        </div>
        {linkPopover}
      </>
    );
  }

  return (
    <>
      <div
        ref={railRef}
        className={"email-builder-canvas-rail email-builder-canvas-rail--horizontal"}
        data-editor-chrome
        data-text-toolbar
        role="toolbar"
        aria-label={ariaLabel}
        data-email-builder-formatting-actions={blockId}
        {...{ [EMAIL_BUILDER_FORMATTING_RAIL_ATTR]: '' }}
        onPointerDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}>
        {buttons}
      </div>
      {linkPopover}
    </>
  );
}
